// @ts-nocheck
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { MessageCircle, Send, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

type SendResult =
  | { ok: true; message_sid: string; status: string; to: string; template_name?: string | null }
  | { ok: false; error: string; twilio_error?: unknown; twilio_status?: number };

interface TemplateOption {
  name: string;
  content_sid: string;
  broadcast_type: string;
  approved_at: string | null;
  variable_schema: Record<string, string>;
}

export default function WhatsAppTestPage() {
  const [to, setTo] = useState('');
  const [body, setBody] = useState('Hola desde LIDER IA. Este es un mensaje de prueba.');
  const [mode, setMode] = useState<'freeform' | 'template'>('freeform');
  const [templateName, setTemplateName] = useState<string>('');
  const [variablesJson, setVariablesJson] = useState<string>('{\n  "1": "Andrés"\n}');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<SendResult | null>(null);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [dispatchResult, setDispatchResult] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingTemplates(true);
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('name, content_sid, broadcast_type, approved_at, variable_schema')
        .order('name');
      if (cancelled) return;
      if (error) {
        toast({ title: 'No se pudieron cargar templates', description: error.message, variant: 'destructive' });
      } else {
        setTemplates((data ?? []) as TemplateOption[]);
      }
      setLoadingTemplates(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedTemplate = templates.find(t => t.name === templateName);

  useEffect(() => {
    if (!selectedTemplate) return;
    const schema = selectedTemplate.variable_schema ?? {};
    const keys = Object.keys(schema);
    if (keys.length === 0) {
      setVariablesJson('{}');
      return;
    }
    const example: Record<string, string> = {};
    for (const k of keys) example[k] = `<${schema[k]}>`;
    setVariablesJson(JSON.stringify(example, null, 2));
  }, [templateName, selectedTemplate]);

  const handleSend = async () => {
    if (!to.trim()) {
      toast({ title: 'Falta el número', description: 'Ingresá un número receptor', variant: 'destructive' });
      return;
    }
    if (mode === 'freeform' && !body.trim()) {
      toast({ title: 'Falta el body', description: 'Ingresá el texto', variant: 'destructive' });
      return;
    }
    if (mode === 'template' && !templateName) {
      toast({ title: 'Falta el template', description: 'Elegí un template del catálogo', variant: 'destructive' });
      return;
    }

    let contentVariables: Record<string, string> | undefined;
    if (mode === 'template') {
      try {
        contentVariables = JSON.parse(variablesJson || '{}');
      } catch (e: any) {
        toast({ title: 'JSON inválido', description: e?.message ?? 'Revisá el formato', variant: 'destructive' });
        return;
      }
    }

    setSending(true);
    setLastResult(null);
    try {
      const payload = mode === 'freeform'
        ? { to: to.trim(), body: body.trim(), triggered_by: 'admin_test_freeform' }
        : { to: to.trim(), template_name: templateName, content_variables: contentVariables, triggered_by: 'admin_test_template' };

      const { data, error } = await supabase.functions.invoke('send-whatsapp', { body: payload });
      if (error) {
        const errMsg = error.message ?? 'Error desconocido';
        setLastResult({ ok: false, error: errMsg });
        toast({ title: 'Error', description: errMsg, variant: 'destructive' });
        return;
      }
      if (data?.ok) {
        setLastResult(data as SendResult);
        toast({ title: 'Enviado', description: `SID: ${data.message_sid} — ${data.status}` });
      } else {
        setLastResult({ ok: false, error: data?.error ?? 'Sin ok en respuesta', twilio_error: data?.twilio_error, twilio_status: data?.twilio_status });
        toast({ title: 'Twilio rechazó', description: data?.error ?? 'Ver detalle abajo', variant: 'destructive' });
      }
    } catch (e: any) {
      setLastResult({ ok: false, error: e?.message ?? String(e) });
      toast({ title: 'Error de red', description: e?.message ?? String(e), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const runDispatcher = async (dryRun: boolean) => {
    setDispatching(true);
    setDispatchResult(null);
    try {
      // Se invoca con la sesión de super_admin → dispatch-whatsapp-triggers
      // acepta super_admin. dry_run va en el body.
      const { data, error } = await supabase.functions.invoke('dispatch-whatsapp-triggers', {
        body: { dry_run: dryRun },
      });
      if (error) {
        setDispatchResult({ error: error.message });
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
      } else {
        setDispatchResult(data);
        toast({
          title: dryRun ? 'Dry run OK' : 'Dispatcher ejecutado',
          description: dryRun ? `Elegibles: ${data?.opted_in_count ?? '?'}` : 'Ver resultado abajo',
        });
      }
    } catch (e: any) {
      setDispatchResult({ error: e?.message ?? String(e) });
      toast({ title: 'Error de red', description: e?.message ?? String(e), variant: 'destructive' });
    } finally {
      setDispatching(false);
    }
  };

  return (
    <div className="container mx-auto py-8 max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MessageCircle className="w-6 h-6 text-primary" />
          Test de WhatsApp (Twilio)
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Página admin para probar la integración Twilio. Solo super_admin. Se dejará hasta que el motor de triggers esté rodando en piloto.
        </p>
      </div>

      <Alert>
        <AlertTriangle className="w-4 h-4" />
        <AlertTitle>Antes de mandar</AlertTitle>
        <AlertDescription>
          Sandbox: el número receptor debe haber hecho <code className="bg-muted px-1 rounded">join &lt;palabra&gt;</code> (Twilio Console → Messaging → Try it out). Sender aprobado: no hace falta join.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Enviar mensaje</CardTitle>
          <CardDescription>Se autentica automáticamente con tu sesión de super_admin.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="to">Número receptor (E.164 o local Colombia)</Label>
            <Input
              id="to"
              placeholder="+573001234567 o 3001234567"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={sending}
            />
          </div>

          <Tabs value={mode} onValueChange={(v) => setMode(v as 'freeform' | 'template')}>
            <TabsList>
              <TabsTrigger value="freeform">Texto libre (sandbox)</TabsTrigger>
              <TabsTrigger value="template">Template aprobado</TabsTrigger>
            </TabsList>

            <TabsContent value="freeform" className="space-y-2 pt-4">
              <Label htmlFor="body">Cuerpo del mensaje</Label>
              <Textarea
                id="body"
                rows={4}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={sending}
              />
              <p className="text-xs text-muted-foreground">
                Texto libre solo funciona en Sandbox y dentro de ventana 24h post-mensaje del user. Fuera de ventana, usar template.
              </p>
            </TabsContent>

            <TabsContent value="template" className="space-y-3 pt-4">
              <div className="space-y-2">
                <Label htmlFor="template">Template</Label>
                <Select value={templateName} onValueChange={setTemplateName} disabled={sending || loadingTemplates}>
                  <SelectTrigger id="template">
                    <SelectValue placeholder={loadingTemplates ? 'Cargando...' : 'Elegí un template'} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.name} value={t.name}>
                        <span className="flex items-center gap-2">
                          <span>{t.name}</span>
                          {t.approved_at
                            ? <Badge variant="secondary" className="text-xs">APPROVED</Badge>
                            : <Badge variant="outline" className="text-xs">PENDING</Badge>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTemplate && !selectedTemplate.approved_at && (
                  <p className="text-xs text-amber-600">
                    ⚠ Template aún no aprobado por Meta. Twilio puede rechazar el envío.
                  </p>
                )}
              </div>

              {selectedTemplate && Object.keys(selectedTemplate.variable_schema ?? {}).length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="vars">Variables (JSON)</Label>
                  <Textarea
                    id="vars"
                    rows={6}
                    value={variablesJson}
                    onChange={(e) => setVariablesJson(e.target.value)}
                    disabled={sending}
                    className="font-mono text-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Reemplazá los <code>{'<...>'}</code> por valores reales. Schema esperado: <code className="text-[10px]">{JSON.stringify(selectedTemplate.variable_schema)}</code>
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <Button onClick={handleSend} disabled={sending || !to.trim()}>
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Enviar
          </Button>
        </CardContent>
      </Card>

      {lastResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {lastResult.ok
                ? <><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Enviado</>
                : <><AlertTriangle className="w-5 h-5 text-destructive" /> Error</>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">{JSON.stringify(lastResult, null, 2)}</pre>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" /> Motor de triggers (dispatcher)
          </CardTitle>
          <CardDescription>
            Ejecuta <code>dispatch-whatsapp-triggers</code> con tu sesión de super_admin.
            "Dry run" solo cuenta los participantes elegibles (opt-in + phone en cohorte activa).
            "Ejecutar" evalúa los 6 triggers de cron y envía — respeta el modo (off/pilot/live).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => runDispatcher(true)} disabled={dispatching}>
              {dispatching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Dry run (contar elegibles)
            </Button>
            <Button onClick={() => runDispatcher(false)} disabled={dispatching}>
              {dispatching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Ejecutar dispatcher
            </Button>
          </div>
          {dispatchResult && (
            <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">{JSON.stringify(dispatchResult, null, 2)}</pre>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
